/**
 * Header shell (Angular 18)
 *
 * The outer header chrome shared by every header mode: the `.header` /
 * `.header-inner` wrapper and the conditional company-name line. The per-mode
 * body (minimal close link, print controls, application nav) is projected via
 * `<ng-content>`.
 *
 * Migrated from the AngularJS `header` directive's shell template
 * (apps/b2b-ui/common/directives/header/header.tpl.html). Styling is provided
 * by the existing global `header.less` (`.header`, `.header-inner`,
 * `.header-company`, …) — no component styles are needed.
 *
 * Uses `@Input()` decorators (not signal `input()`): the hybrid app compiles
 * with ts-loader + Angular JIT, which only discovers decorator-declared
 * inputs/outputs — signal inputs are invisible to it and fail at runtime
 * (NG0303). This matches the repo's existing migrated components.
 *
 * Usage (Angular): `<app-header-shell [isLoggedIn]="…" [useCart]="…" [companyName]="…" [print]="…"><…/></app-header-shell>`
 */
import { Component, Input, inject } from '@angular/core';
import { NgIf } from '@angular/common';

import { STATE_PORT } from '../../../core/utils';

/**
 * The company-name line is shown only for a logged-in session that is not in
 * cart mode — preserves the original `header.isLoggedIn && !header.useCart`.
 */
export function isCompanyLineVisible(isLoggedIn: boolean, useCart: boolean): boolean {
  return isLoggedIn && !useCart;
}

@Component({
  selector: 'app-header-shell',
  standalone: true,
  imports: [NgIf],
  template: require('./header-shell.component.html'),
})
export class HeaderShellComponent {
  /** Whether a user session is active (drives the company-name line). */
  @Input() isLoggedIn = false;

  /** Whether the cart UI is enabled; when true the company-name line is hidden. */
  @Input() useCart = false;

  /** Company display name rendered in the company-name line. */
  @Input() companyName = '';

  /**
   * Adds the `print` class to the chrome so it is hidden via `@media print`
   * (the minimal and print modes set this; application mode leaves it off).
   * Mirrors the original `<header class="print">` that merged onto `.header`.
   */
  @Input() print = false;

  private readonly state = inject(STATE_PORT);

  /** Whether the company-name line is visible. */
  get showCompany(): boolean {
    return isCompanyLineVisible(this.isLoggedIn, this.useCart);
  }

  /** Navigate to company settings (was `data-ui-sref="application.account.company"`). */
  onCompanyClick(): void {
    this.state.go('application.account.company');
  }
}
